// src/components/TipoEnvase/TipoEnvaseIndex.tsx

import React, { useState, useEffect, useRef } from "react";
import * as S from "./TipoEnvase.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import axios, { AxiosError } from "axios";
import { ApiError } from "../../../services/apiError";
import { apiService } from "../../../services/apiService";
import { TipoEnvase } from "../../../dtos/TipoEnvase";
import { useNavigate } from "react-router-dom";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado. Revisa tu conexión de red." };
};

const TipoEnvaseIndex = () => {
  const [dataSource, setDataSource] = useState<TipoEnvase[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<TipoEnvase> | null>(
    null,
  );
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TipoEnvase | null>(null);
  const navigate = useNavigate();
  const formRef = useRef<FormInstance | null>(null);
  const dataGridRef = useRef<any>(null);
  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Tipos de envases");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Tipos_de_envases.xlsx",
    );
  };
  const fetchTiposEnvase = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<TipoEnvase[]>(
        "/api/Catalogos/TiposEnvase",
      );
      setDataSource(response);
    } catch (err: any) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiposEnvase();
  }, []);

  const handleAdd = () => {
    setEditingItem({ estatusActivo: true });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: TipoEnvase) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: TipoEnvase) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.patch(
          `/api/Catalogos/TiposEnvase/${itemToDelete.idTipoEnvase}/cambiastatus`,
          "",
        );
        fetchTiposEnvase();
        notify("Cambio de estatus realizado con éxito.", "success", 2000);
      } catch (error) {
        notify("Error al cambiar el estatus del registro.", "error", 3000);
      } finally {
        setIsDeletePopupVisible(false);
        setItemToDelete(null);
      }
    }
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000,
      );
      return;
    }

    const formData = form.option("formData") as TipoEnvase;

    try {
      if (formData.idTipoEnvase) {
        // Actualizar (PUT)
        const response = await apiService.put<TipoEnvase, TipoEnvase>(
          `/api/Catalogos/TiposEnvase/${formData.idTipoEnvase}`,
          formData,
        );
        setDataSource((prev) =>
          prev.map((item) =>
            item.idTipoEnvase === formData.idTipoEnvase ? response : item,
          ),
        );
        notify("Registro actualizado con éxito.", "success", 2000);
      } else {
        // Crear (POST)
        const response = await apiService.post<TipoEnvase, TipoEnvase>(
          "/api/Catalogos/TiposEnvase",
          formData,
        );
        setDataSource((prev) => [...prev, response]);
        notify("Registro creado con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
    } catch (error) {
      notify("Error al guardar el registro.", "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isActive = cellInfo.value === true;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as TipoEnvase;
    const isActive = item.estatusActivo;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(item)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      {/* CAMBIO 1: Activa los estilos globales para el título */}
      <S.GlobalPopupStyles />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <S.Header>Catálogo tipos de envases</S.Header>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="idTipoEnvase" // <-- CORRECCIÓN: La clave es 'id'
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <Paging defaultPageSize={10} />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} allowSearch={true} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Exportar Excel",
              icon: "xlsxfile",
              stylingMode: "contained",
              type: "default",
              onClick: handleExportExcel,
            }}
          />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir Tipo Envase",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder="Buscar..."
        />
        {/*<Column dataField="tipoDeEnvase" caption="Tipo envase" width={120} /> */}
        <Column dataField="descripcion" caption="Descripción" width={300} />
        <Column dataField="claseEnvase" caption="Clase envase" width={120} />
        <Column
          dataField="capacidad"
          caption="Capacidad"
          alignment="right"
          visible={false}
          width={300}
        />
        <Column
          dataField="estatusActivo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={300}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={300}
          cellRender={ActionsCell}
          allowExporting={false}
        />
      </DataGrid>

      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          editingItem?.idTipoEnvase
            ? `Editar Tipo de Envase: ${editingItem.tipoDeEnvase}`
            : "Nuevo tipo envase"
        }
        width={500}
        height="auto"
        className="custom-form-popup" // <-- Clase para que funcionen los estilos
        // CAMBIO 2: Se elimina por completo la propiedad 'toolbarItems'
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            {/* CORRECCIÓN: Campos del formulario ajustados a la interface TipoEnvase */}
            <SimpleItem dataField="tipoDeEnvase">
              <Label text="Tipo envase" />
              <RequiredRule message="El código es requerido." />
            </SimpleItem>
            <SimpleItem
              dataField="descripcion"
              editorType="dxTextArea"
              editorOptions={{ height: 100 }}
            >
              <Label text="Descripción" />
              <RequiredRule message="La descripción es requerida." />
            </SimpleItem>
            <SimpleItem dataField="claseEnvase">
              <Label text="Clase envase" />
              <RequiredRule message="La UM es requerida." />
            </SimpleItem>
            {/*<SimpleItem dataField="capacidad" editorType="dxNumberBox">
                            <Label text="Capacidad" />
                            <RequiredRule message="La capacidad es requerida." />
                            <NumericRule message="Debe ser un número." />
                        </SimpleItem>*/}
          </Form>
        </S.FormContainer>

        {/* CAMBIO 3: Se añade el footer personalizado con nuestros botones */}
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      {/* Popup de confirmación */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle={true}
        title={`Confirmar ${
          itemToDelete?.estatusActivo ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${
                itemToDelete?.estatusActivo ? "Desactivar" : "Activar"
              }`,
              type: itemToDelete?.estatusActivo ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmDelete,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {itemToDelete?.estatusActivo ? "desactivar" : "activar"}
            </strong>{" "}
            el tipo de envase
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.descripcion}"?
          </p>
        </div>
      </Popup>
    </S.PageWrapper>
  );
};

export default TipoEnvaseIndex;
